import '../css/BrandLogo.css';

/** Shown when the club has not uploaded a logo yet (Config -> Branding). */
const DEFAULT_PLACEHOLDER = '🏓';

/**
 * The club's mark, as it appears in every header.
 *
 * The drop shadow is a SECOND copy of the same image, blurred and sitting
 * behind the real one. CSS filters cannot sample colour from artwork —
 * `drop-shadow()` paints one flat colour, so a teal logo and a red one would
 * cast an identical shadow — but blurring the image itself makes the glow out
 * of the logo's own pixels, so it follows whatever the club uploads. Both
 * copies share a URL, so the browser fetches the file once.
 *
 * Sizing comes from the caller (a class or a style), which is why nothing here
 * hard-codes a width: the landing header, the admin login header and the
 * sidebar all run different sizes off the same component.
 *
 * @param {object} props
 * @param {string} [props.src] - config.logoUrl; falsy renders the placeholder
 * @param {string} [props.alt] - club name; '' on the placeholder, which is decorative
 * @param {string} [props.className] - sizing/layout class, e.g. 'lp-logo'
 * @param {string} [props.placeholder] - emoji shown when there is no logo
 * @param {object} [props.style] - inline sizing, for callers with no class
 * @returns {JSX.Element}
 */
function BrandLogo({ src, alt = '', className = '', placeholder = DEFAULT_PLACEHOLDER, style }) {
  const classes = ['jt-logo', className].filter(Boolean).join(' ');

  if (!src) {
    return (
      <div className={`${classes} jt-logo-placeholder`} style={style} aria-hidden="true">
        {placeholder}
      </div>
    );
  }

  return (
    <span className={classes} style={style}>
      {/* aria-hidden + empty alt: the glow is the same picture twice, and a
          screen reader announcing the club name twice is worse than silence. */}
      <img src={src} alt="" aria-hidden="true" className="jt-logo-glow" />
      <img src={src} alt={alt} className="jt-logo-img" />
    </span>
  );
}

export default BrandLogo;
